import React, { useEffect } from 'react';
import AssemblyReservationForm from './AssemblyReservationForm';
import AssemblySubForm from './AssemblySubForm';
import TreeComponentSpace, { RootNode, TreeNode } from './TreeComponentSpace';
import {
  AssemblyComponentClass,
  AssemblyComponentStatus,
  AssemblyStatus,
  IAssembly,
  IAssemblyQueryResult,
  IAssemblyQueryVariables,
  IAssemblyStructure,
  IAssemblyStructureNode,
  IAssemblyStructureWithExpandedNodesQueryResult,
  IAssemblyStructureWithExpandedNodesQueryVariables,
  IGenerateAssemblyRequirementDetailForSubAssemblyInput,
  IGenerateAssemblyRequirementDetailForSubAssemblyMutationResult,
} from '../../graphql/schema/p4types';
import { useParams } from 'react-router';
import Assembly from '../../graphql/entities/product/Assembly';
import { useLazyQuery, useQuery } from '@apollo/client';
import { useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';
import AssemblyStructureWithExpandedNodesQuery from '../../graphql/queries/product/AssemblyStructureWithExpandedNodesQuery.graphql';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { p4Tokens } from '../../styles/themes/default/p4FluentTheme';
import { MenuItem } from 'react-contextmenu';
import { useBackgroundMutation } from '../../utils/BackgroundJobs';
import { GenerateAssemblyRequirementDetailForSubAssembly } from '../../graphql/mutations/product/GenerateAssembluRequirementDetailForSubAssembly.graphql';
import getClientMutationId from '../../utils/getClientMutationId';
import { cloneDeep } from '@apollo/client/utilities';
import { AssemblyTreeContextType, useAssemblyTreeContext } from './AssemblyTreeContext';

const getSubAssemblyStatusIcon = (assembly: IAssemblyStructureNode): { color: string; type: string } => {
  let icon = { color: 'default', type: 'info' };
  const status = assembly?.assignedAssembly.status;
  const badgeText = assembly?.assignedAssembly.orderNumber;

  switch (status) {
    case AssemblyStatus.Draft:
      icon = {
        type: 'assignment',
        color: p4Tokens.statusFlagDanger,
        text: badgeText,
      };
      break;
    case AssemblyStatus.Unprocessed:
      icon = {
        type: 'error',
        color: p4Tokens.statusFlagDanger,
        text: badgeText,
      };
      break;
    case AssemblyStatus.InAssembly:
    case AssemblyStatus.PartComplete:
      icon = {
        type: 'access_time',
        color: p4Tokens.statusFlagWarning,
        text: badgeText,
      };
      break;
    case AssemblyStatus.Complete:
      icon = {
        type: 'check_circle',
        color: p4Tokens.statusFlagSuccess,
        text: badgeText,
      };
      break;
  }

  return icon;
};

const findNodeByRowPath = (nodes: IAssemblyStructureNode[], rowPath: string): TreeNode<IAssemblyStructureNode> => {
  for (const node of nodes) {
    if (node.rowPath === rowPath) {
      return node;
    }

    if (node?.childNodes) {
      const childNode = findNodeByRowPath(node.childNodes, rowPath);
      if (childNode != null) {
        return childNode;
      }
    }
  }

  return null;
};

const findNodeByNodeId = (nodes: [], nodeId: number): TreeNode<IAssemblyStructureNode> => {
  for (const node of nodes) {
    if (node.nodeId == nodeId) {
      return node;
    }

    if (node?.childNodes) {
      const childNode = findNodeByNodeId(node.childNodes, nodeId);
      if (childNode != null) {
        return childNode;
      }
    }
  }

  return null;
};

const AssemblyReservationTreeSpace = () => {
  const params = useParams();
  const handleQueryError = useComponentQueryErrorHandling();
  const assemblyId = params?.id;

  const {
    assembly,
    setAssembly,
    setReservationAssembly,
    setAssemblyNodes,
    assemblyProduct,
    setAssemblyProduct,
    assemblyStructure,
    setAssemblyStructure,
    assemblyReservations,
    selectedComponent,
    setSelectedComponent,
    setUpdatedNodes,
    reserveAll,
    expanded,
    setExpanded,
    updatedNodes,
  } = useAssemblyTreeContext() as AssemblyTreeContextType;

  /* Queries/Mutations */
  const [fetchAssemblyStructureWithExpandedNodesQuery] = useLazyQuery<
    IAssemblyStructureWithExpandedNodesQueryVariables,
    IAssemblyStructureWithExpandedNodesQueryResult
  >(AssemblyStructureWithExpandedNodesQuery, {
    onError: handleQueryError,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const newAssemblyStructure = cloneDeep(data?.data);
      const nodes = newAssemblyStructure?.nodes;
      let expandedNodes = newAssemblyStructure?.expandedNodes;
      expandedNodes = sortNodesByRowPath(expandedNodes);
      addNodesToTree(nodes, expandedNodes);
      setAssemblyStructure(newAssemblyStructure);
    },
  });

  const addNodesToTree = (nodes: IAssemblyStructureNode[], nodesToAdd: IAssemblyStructureNode[]) => {
    //first thing we need to do is sort the nodesToAdd in path order do for example
    //1.3.1, 1.2.1, 1.2.1.1 should be sorted shortest first i.e. 1.2.1, 1.3.1, 1.2.1.1 that
    //way we know the parent node of 1.2.1.1 should be added first
    sortNodesByRowPath(nodesToAdd);
    sortNodesByRowPath(nodesToAdd);

    //add the nodes to the assembly structure based on the nodes address
    for (const nodeToAddTo of nodesToAdd) {
      const parentRowPath = nodeToAddTo.rowPath;
      // the first number in the path is the root assembly
      let node = findNodeByRowPath(nodes, parentRowPath);
      node.childNodes = nodeToAddTo.childNodes;
    }
  };

  const sortNodesByRowPath = (nodes: IAssemblyStructureNode[]): Array<IAssemblyStructureNode> => {
    return nodes.sort((a, b) => {
      // if the length is the same, sort numerically
      const rowPathA = a.rowPath.split('.').map(Number);
      const rowPathB = b.rowPath.split('.').map(Number);

      // sort by length first
      if (rowPathA.length !== rowPathB.length) {
        return rowPathA.length - rowPathB.length;
      }

      // if lengths are the same, sort numerically
      for (let i = 0; i < rowPathA.length; i++) {
        if (rowPathA[i] !== rowPathB[i]) {
          return rowPathA[i] - rowPathB[i];
        }
      }

      // if all levels are equal, consider them the same
      return 0;
    });
  };

  const [generateSubAssemblyShortage] = useBackgroundMutation<
    IGenerateAssemblyRequirementDetailForSubAssemblyMutationResult,
    IGenerateAssemblyRequirementDetailForSubAssemblyInput
  >(GenerateAssemblyRequirementDetailForSubAssembly, {
    fetchPolicy: 'network-only',
  });

  useQuery<IAssemblyQueryResult, IAssemblyQueryVariables>(Assembly.instance!, {
    variables: {
      id: assemblyId,
      startingRowPath: '1',
    },
    skip: !assemblyId,
    onError: handleQueryError,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      setAssembly(data?.data as IAssembly);
      setReservationAssembly(data?.data as IAssembly);
      setAssemblyProduct(data?.data?.product);
      fetchAssemblyStructure(expanded);
    },
  });

  const [fetchSubAssembly] = useLazyQuery<IAssemblyQueryResult, IAssemblyQueryVariables>(Assembly.instance!, {
    variables: selectedComponent?.assembly,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    fetchAssemblyStructure(expanded);
  }, [assemblyReservations]);

  const fetchAssemblyStructure = (expandedNodes: Array<string>) => {
    const rowPaths = expandedNodes
      .map((nodeId) => findNodeByNodeId(assemblyStructure?.nodes, nodeId)?.rowPath)
      .filter((rowPath) => rowPath !== undefined);
    fetchAssemblyStructureWithExpandedNodesQuery({
      variables: {
        id: assemblyId,
        rowPaths: rowPaths,
      },
    });
  };

  useEffect(() => {
    if (!assembly || !selectedComponent) {
      return;
    }

    if (selectedComponent.assignedStatus === AssemblyComponentStatus.Unassigned) {
      setReservationAssembly(assembly);
    } else {
      fetchSubAssembly().then((response) => {
        if (response?.data?.data) {
          setReservationAssembly(response.data.data as IAssembly);
        }
      });
    }
  }, [assembly, selectedComponent]);

  useEffect(() => {
    if (assembly && assemblyProduct) {
      let iconColour;
      switch (assembly.status) {
        case AssemblyStatus.Complete:
          iconColour = p4Tokens.statusFlagSuccess;
          break;
        case AssemblyStatus.Draft:
          iconColour = p4Tokens.statusFlagBasic;
          break;
        case AssemblyStatus.InAssembly:
          iconColour = p4Tokens.statusFlagWarning;
          break;
        case AssemblyStatus.PartComplete:
          iconColour = p4Tokens.statusFlagWarning;
          break;
        case AssemblyStatus.Unprocessed:
          iconColour = p4Tokens.statusFlagBasic;
          break;
      }

      const rootNode: RootNode<IAssemblyStructure> = {
        value: assemblyId,
        rootCategory: null,
        sortOrder: 1,
        label: assemblyProduct?.reference,
        showCheckbox: false,
        children: [],
        className: '',
        data: assemblyStructure,
        icon: <FontAwesomeIcon icon={'wrench'} color={iconColour} />,
        showBadge: false,
      };
      setAssemblyNodes([rootNode]);
    }
  }, [assemblyStructure]);

  const createSubAssemblyShortage = (nodeId: number, fullQty: boolean) => {
    const node = findNodeByNodeId(assemblyStructure?.nodes, nodeId);
    const criteria: IAssemblyRequirementDetailForSubAssyCriteriaInput = {
      parentAssembly: { id: node?.assembly.id },
      productComponent: { id: nodeId },
      fullAmount: fullQty,
    };

    generateSubAssemblyShortage({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          creationCriteria: criteria,
        },
      },
    });
  };

  const nodifyAssemblyStructureNode = (info: IAssemblyStructureNode): TreeNode<IAssemblyStructureNode> => {
    let iconColour;
    let status: string;
    switch (info.reservedStatus) {
      case AssemblyComponentStatus.Unreserved:
        iconColour = p4Tokens.statusFlagBasic;
        status = 'has no reservations';
        break;
      case AssemblyComponentStatus.PartiallyReserved:
        iconColour = p4Tokens.statusFlagWarning;
        status = 'has some reservations';
        break;
      case AssemblyComponentStatus.FullyReserved:
        iconColour = p4Tokens.statusFlagSuccess;
        status = 'is completely reserved';
        break;
      default:
        iconColour = p4Tokens.statusFlagBasic;
        status = '';
    }

    let icon;
    switch (info.componentClass) {
      case AssemblyComponentClass.Component:
        icon = (
          <TooltipHost content={`Component ${status}`}>
            <FontAwesomeIcon icon={'cog'} color={iconColour} />
          </TooltipHost>
        );
        break;
      case AssemblyComponentClass.Subassembly:
        icon = (
          <TooltipHost content={`Sub-assembly ${status}`}>
            <FontAwesomeIcon icon={'wrench'} color={iconColour} />
          </TooltipHost>
        );
        break;
      case AssemblyComponentClass.Process:
        icon = (
          <TooltipHost content={`Process ${status}`}>
            <FontAwesomeIcon icon={'project-diagram'} color={iconColour} />
          </TooltipHost>
        );
        break;
      default:
        icon = <FontAwesomeIcon icon={'cog'} color={iconColour} />;
    }

    let badgeInfo;
    if (info?.assignedAssembly) {
      badgeInfo = getSubAssemblyStatusIcon(info);
    }

    let enableContextMenu = false;
    let menuItems = [];
    if (info.componentClass === AssemblyComponentClass.Subassembly && !info?.assignedAssembly && info?.hasShortage) {
      if (
        info?.assignedStatus === AssemblyComponentStatus.Unassigned &&
        info?.reservedStatus === AssemblyComponentStatus.Unreserved
      ) {
        menuItems.push(
          <MenuItem
            key={'createAssemblyShortageFullAmount'}
            onClick={() => createSubAssemblyShortage(info.nodeId, true)}
          >
            {'Add Assembly Shortage (Full Amount)'}
          </MenuItem>
        );
      }
      menuItems.push(
        <MenuItem
          key={'createAssemblyShortageShortAmount'}
          onClick={() => createSubAssemblyShortage(info.nodeId, false)}
        >
          {'Add Assembly Shortage (Short Amount)'}
        </MenuItem>
      );
      enableContextMenu = true;
    }

    return {
      data: info,
      value: info?.nodeId?.toString(),
      rootCategory: info.rootComponentId,
      sortOrder: info.sortOrder,
      label: info.label,
      showCheckbox: false,
      hasChildren: info.componentClass === AssemblyComponentClass.Subassembly,
      children: [],
      className: 'rct-node-not-updated',
      parent: info.parentComponentId,
      icon: icon,
      updated: false,
      showBadge: info.assignedAssembly && info.componentClass === AssemblyComponentClass.Subassembly,
      badgeInfo: badgeInfo,
      enableContextMenu: enableContextMenu,
      actionButtons: menuItems,
    };
  };

  const onExpandNode = (expanded: [], node: TreeNode<IAssemblyStructureNode>) => {
    fetchAssemblyStructure(expanded);
    setExpanded(expanded);
  };

  const selectComponent = (componentId: string) => {
    const component = findNodeByNodeId(assemblyStructure?.nodes, componentId);
    if (component) {
      setSelectedComponent(component);
    }
  };

  useEffect(() => {
    const reservationsWithQuantities = [];
    if (reserveAll && assemblyStructure) {
      for (const node of assemblyStructure.nodes) {
        if (
          node?.freeStock &&
          (node?.reservedStatus === AssemblyComponentStatus.PartiallyReserved ||
            node?.reservedStatus === AssemblyComponentStatus.Unreserved)
        ) {
          reservationsWithQuantities.push(node.nodeId);
        }
      }

      const nodeSet = new Set([...updatedNodes, ...reservationsWithQuantities]);
      setUpdatedNodes([...nodeSet]);
    }
  }, [reserveAll, assemblyStructure]);

  return (
    <TreeComponentSpace<IAssemblyStructureNode, IAssemblyStructure>
      form={AssemblyReservationForm}
      subForm={AssemblySubForm}
      constructNode={nodifyAssemblyStructureNode}
      onSelectNode={selectComponent}
      onExpandNode={onExpandNode}
    />
  );
};

export default AssemblyReservationTreeSpace;
